import { useState, useEffect, useCallback } from "react";
import { T, Icon, useApi } from "../AdminPanel";

export default function UsersPage({ token, toast, onExpired }) {
  const { get, del, patch, post } = useApi(token, onExpired);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", username: "", password: "", setup_2fa: true });
  const [adding, setAdding] = useState(false);
  const [changingRole, setChangingRole] = useState(null);
  
  // 2FA модальные окна
  const [setup2FAUser, setSetup2FAUser] = useState(null);
  const [setup2FAData, setSetup2FAData] = useState(null);
  const [setup2FACode, setSetup2FACode] = useState("");
  const [setup2FALoading, setSetup2FALoading] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(null);
  const [disabling2FA, setDisabling2FA] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setUsers(await get("/users/"));
    } catch {
      toast("Ошибка загрузки", "error");
    } finally {
      setLoading(false);
    }
  }, [get, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleActive = async (user) => {
    try {
      await patch(`/users/${user.id}/activate?is_active=${!user.is_active}`, {});
      toast("Статус изменён", "success");
      load();
    } catch {
      toast("Ошибка", "error");
    }
  };

  const changeRole = async (user, newRole) => {
    try {
      await patch(`/users/${user.id}/role`, { role: newRole });
      toast(`Роль изменена на ${newRole === "admin" ? "Администратор" : "Пользователь"}`, "success");
      setChangingRole(null);
      load();
    } catch (e) {
      toast(e.message || "Ошибка", "error");
    }
  };

  const addUser = async () => {
    if (!newUser.email || !newUser.username || !newUser.password) {
      toast("Заполните все поля", "error");
      return;
    }
    if (newUser.password.length < 8) {
      toast("Пароль должен быть не менее 8 символов", "error");
      return;
    }
    setAdding(true);
    try {
      const created = await post("/auth/register", {
        email: newUser.email,
        username: newUser.username,
        password: newUser.password,
      });
      
      toast("Пользователь создан", "success");
      setShowAddModal(false);
      setNewUser({ email: "", username: "", password: "", setup_2fa: true });
      load();
      
      // Если нужно сразу настроить 2FA
      if (newUser.setup_2fa) {
        start2FASetup(created);
      }
    } catch (e) {
      toast(e.message || "Ошибка при создании", "error");
    } finally {
      setAdding(false);
    }
  };

  const start2FASetup = async (user) => {
    setSetup2FALoading(true);
    try {
      const data = await post(`/admin/2fa/setup/${user.id}`, {});
      setSetup2FAUser(user);
      setSetup2FAData(data);
    } catch (e) {
      toast(e.message || "Ошибка настройки 2FA", "error");
    } finally {
      setSetup2FALoading(false);
    }
  };

  const confirm2FASetup = async () => {
    if (!setup2FACode) {
      toast("Введите код из приложения", "error");
      return;
    }
    setSetup2FALoading(true);
    try {
      await post(`/admin/2fa/enable/${setup2FAUser.id}`, { code: setup2FACode });
      toast(`2FA для ${setup2FAUser.username} успешно включена!`, "success");
      setSetup2FAUser(null);
      setSetup2FAData(null);
      setSetup2FACode("");
      load();
    } catch (e) {
      toast(e.message || "Неверный код", "error");
    } finally {
      setSetup2FALoading(false);
    }
  };

  const disable2FA = async (user) => {
    try {
      await post(`/admin/2fa/disable/${user.id}`, {});
      toast(`2FA для ${user.username} отключена`, "success");
      setDisabling2FA(null);
      load();
    } catch (e) {
      toast(e.message || "Ошибка", "error");
    }
  };

  const regenerateBackupCodes = async (user) => {
    try {
      const data = await post(`/admin/2fa/regenerate-backup/${user.id}`, {});
      setShowBackupCodes({ user, codes: data.backup_codes });
    } catch (e) {
      toast(e.message || "Ошибка", "error");
    }
  };

  const confirmDelete = async (id) => {
    try {
      await del(`/users/${id}`);
      toast("Пользователь удалён", "success");
      setDeleting(null);
      setUsers((prev) => prev.filter((u) => u.id !== id));
    } catch (e) {
      toast(e.message, "error");
    }
  };

  return (
    <div>
      <div className="adm-eyebrow">Система доступа</div>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          marginBottom: 28,
        }}
      >
        <h1
          style={{
            fontFamily: "'Oswald', sans-serif",
            fontSize: 32,
            fontWeight: 400,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          Пользователи
        </h1>
        <div style={{ display: "flex", gap: 12 }}>
          <button className="adm-btn" onClick={load}>
            {Icon.Refresh} Обновить
          </button>
          <button
            className="adm-btn adm-btn--primary"
            onClick={() => setShowAddModal(true)}
          >
            {Icon.Plus} Добавить
          </button>
        </div>
      </div>

      <div className="adm-card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div className="adm-empty">
            <span className="adm-spinner" />
          </div>
        ) : (
          <table className="adm-table">
            <thead>
              <tr>
                <th>Пользователь</th>
                <th>Email</th>
                <th>2FA</th>
                <th>Статус</th>
                <th>Роль</th>
                <th>Дата регистрации</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: "50%",
                          background: u.is_superuser ? T.primary : "rgba(18,49,51,0.1)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 12,
                          fontWeight: 500,
                          color: u.is_superuser ? T.white : T.textMuted,
                          flexShrink: 0,
                        }}
                      >
                        {u.username.slice(0, 2).toUpperCase()}
                      </div>
                      <span style={{ fontWeight: 500 }}>{u.username}</span>
                    </div>
                  </td>
                  <td style={{ color: T.textMuted }}>{u.email}</td>
                  <td>
                    {u.totp_enabled ? (
                      <span
                        className="adm-tag"
                        style={{ background: "rgba(16,201,165,0.1)", color: T.accent }}
                      >
                        {Icon.Shield} Включена
                      </span>
                    ) : (
                      <button
                        className="adm-btn adm-btn--sm"
                        onClick={() => start2FASetup(u)}
                        style={{ fontSize: 10 }}
                      >
                        {Icon.Plus} Настроить 2FA
                      </button>
                    )}
                  </td>
                  <td>
                    <span
                      className="adm-tag"
                      style={
                        u.is_active
                          ? { background: "rgba(16,201,165,0.1)", color: T.accent }
                          : { background: T.dangerBg, color: T.danger }
                      }
                    >
                      {u.is_active ? "Активен" : "Заблокирован"}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span
                        className="adm-tag"
                        style={
                          u.role === "admin"
                            ? { background: T.primary, color: T.white }
                            : { background: "rgba(18,49,51,0.08)", color: T.textMuted }
                        }
                      >
                        {u.role === "admin" ? "Администратор" : "Пользователь"}
                      </span>
                      <button
                        className="adm-btn--icon"
                        onClick={() => setChangingRole(u)}
                        title="Изменить роль"
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          color: T.textMuted,
                        }}
                      >
                        {Icon.Edit}
                      </button>
                    </div>
                  </td>
                  <td style={{ color: T.textMuted, fontSize: 12 }}>
                    {new Date(u.created_at).toLocaleDateString("ru-RU")}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      {u.totp_enabled && (
                        <>
                          <button
                            className="adm-btn adm-btn--sm"
                            onClick={() => regenerateBackupCodes(u)}
                            title="Новые резервные коды"
                          >
                            {Icon.Refresh}
                          </button>
                          <button
                            className="adm-btn adm-btn--sm adm-btn--danger"
                            onClick={() => setDisabling2FA(u)}
                            title="Отключить 2FA"
                          >
                            {Icon.Trash}
                          </button>
                        </>
                      )}
                      <button
                        className="adm-btn adm-btn--sm"
                        onClick={() => toggleActive(u)}
                      >
                        {u.is_active ? "Заблокировать" : "Активировать"}
                      </button>
                      <button
                        className="adm-btn adm-btn--sm adm-btn--danger"
                        onClick={() => setDeleting(u)}
                      >
                        {Icon.Trash}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Модальное окно добавления пользователя */}
      {showAddModal && (
        <div
          className="adm-modal-wrap"
          onClick={(e) => e.target === e.currentTarget && setShowAddModal(false)}
        >
          <div className="adm-modal" style={{ maxWidth: 400 }}>
            <div className="adm-modal__header">
              <span className="adm-modal__title">Добавить пользователя</span>
              <button
                className="adm-btn adm-btn--sm"
                onClick={() => setShowAddModal(false)}
              >
                {Icon.Close}
              </button>
            </div>
            <div className="adm-modal__body">
              <div className="adm-form" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div className="adm-field">
                  <label className="adm-label">Email</label>
                  <input
                    className="adm-input"
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    placeholder="user@example.com"
                  />
                </div>
                <div className="adm-field">
                  <label className="adm-label">Имя пользователя</label>
                  <input
                    className="adm-input"
                    type="text"
                    value={newUser.username}
                    onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                    placeholder="username"
                  />
                </div>
                <div className="adm-field">
                  <label className="adm-label">Пароль (мин. 8 символов)</label>
                  <input
                    className="adm-input"
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="••••••••"
                  />
                </div>
                <div className="adm-field">
                  <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={newUser.setup_2fa}
                      onChange={(e) => setNewUser({ ...newUser, setup_2fa: e.target.checked })}
                    />
                    <span className="adm-label" style={{ marginBottom: 0 }}>
                      Сразу настроить 2FA
                    </span>
                  </label>
                </div>
              </div>
            </div>
            <div className="adm-modal__footer">
              <button className="adm-btn" onClick={() => setShowAddModal(false)}>
                Отмена
              </button>
              <button className="adm-btn adm-btn--primary" onClick={addUser} disabled={adding}>
                {adding ? <span className="adm-spinner" /> : Icon.Check}
                {adding ? "Создание..." : "Создать"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно настройки 2FA */}
      {setup2FAUser && setup2FAData && (
        <div
          className="adm-modal-wrap"
          onClick={(e) => e.target === e.currentTarget && setSetup2FAUser(null)}
        >
          <div className="adm-modal" style={{ maxWidth: 500 }}>
            <div className="adm-modal__header">
              <span className="adm-modal__title">Настройка 2FA для {setup2FAUser.username}</span>
              <button className="adm-btn adm-btn--sm" onClick={() => setSetup2FAUser(null)}>
                {Icon.Close}
              </button>
            </div>
            <div className="adm-modal__body">
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontSize: 13, color: T.textMuted, marginBottom: 16 }}>
                  1. Сообщите пользователю эти данные или передайте QR-код
                </p>
                <div style={{ background: T.bg, padding: 16, borderRadius: 4, marginBottom: 16 }}>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: T.textMuted }}>Секретный ключ:</div>
                    <code style={{ fontSize: 12 }}>{setup2FAData.secret}</code>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 8 }}>QR-код:</div>
                    <img
                      src={`data:image/png;base64,${setup2FAData.qr_code}`}
                      alt="QR Code"
                      style={{ width: 160, height: 160, border: `1px solid ${T.border}` }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <p style={{ fontSize: 13, color: T.textMuted, marginBottom: 12 }}>
                  2. Резервные коды (передайте пользователю):
                </p>
                <div
                  style={{
                    background: "#fff3e0",
                    border: "1px solid #ffd699",
                    padding: 16,
                    borderRadius: 4,
                  }}
                >
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {setup2FAData.backup_codes.map((code, idx) => (
                      <code
                        key={idx}
                        style={{
                          background: T.white,
                          padding: "4px 8px",
                          fontFamily: "monospace",
                          fontSize: 11,
                          letterSpacing: 1,
                        }}
                      >
                        {code}
                      </code>
                    ))}
                  </div>
                  <p style={{ fontSize: 11, color: T.textMuted, marginTop: 12 }}>
                    ⚠️ Пользователь должен сохранить эти коды!
                  </p>
                </div>
              </div>

              <div>
                <p style={{ fontSize: 13, color: T.textMuted, marginBottom: 12 }}>
                  3. Попросите пользователя установить приложение (Google Authenticator),
                  отсканировать QR-код и сообщить вам код для подтверждения:
                </p>
                <div style={{ display: "flex", gap: 12 }}>
                  <input
                    type="text"
                    className="adm-input"
                    placeholder="6-значный код"
                    value={setup2FACode}
                    onChange={(e) => setSetup2FACode(e.target.value)}
                    style={{ width: 140 }}
                  />
                  <button
                    className="adm-btn adm-btn--primary"
                    onClick={confirm2FASetup}
                    disabled={setup2FALoading}
                  >
                    {setup2FALoading ? <span className="adm-spinner" /> : Icon.Check}
                    Подтвердить и включить
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно с резервными кодами */}
      {showBackupCodes && (
        <div
          className="adm-modal-wrap"
          onClick={(e) => e.target === e.currentTarget && setShowBackupCodes(null)}
        >
          <div className="adm-modal" style={{ maxWidth: 450 }}>
            <div className="adm-modal__header">
              <span className="adm-modal__title">Новые резервные коды для {showBackupCodes.user.username}</span>
              <button className="adm-btn adm-btn--sm" onClick={() => setShowBackupCodes(null)}>
                {Icon.Close}
              </button>
            </div>
            <div className="adm-modal__body">
              <div
                style={{
                  background: "#fff3e0",
                  border: "1px solid #ffd699",
                  padding: 16,
                  borderRadius: 4,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 12, color: T.text }}>
                  ⚠️ Передайте эти коды пользователю. Старые коды больше не работают!
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {showBackupCodes.codes.map((code, idx) => (
                    <code
                      key={idx}
                      style={{
                        background: T.white,
                        padding: "6px 10px",
                        fontFamily: "monospace",
                        fontSize: 12,
                        letterSpacing: 1,
                      }}
                    >
                      {code}
                    </code>
                  ))}
                </div>
              </div>
            </div>
            <div className="adm-modal__footer">
              <button className="adm-btn adm-btn--primary" onClick={() => setShowBackupCodes(null)}>
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно отключения 2FA */}
      {disabling2FA && (
        <div
          className="adm-modal-wrap"
          onClick={(e) => e.target === e.currentTarget && setDisabling2FA(null)}
        >
          <div className="adm-modal" style={{ maxWidth: 400 }}>
            <div className="adm-modal__header">
              <span className="adm-modal__title">Отключение 2FA</span>
              <button className="adm-btn adm-btn--sm" onClick={() => setDisabling2FA(null)}>
                {Icon.Close}
              </button>
            </div>
            <div className="adm-modal__body">
              <p style={{ fontSize: 14, color: T.textMuted }}>
                Вы уверены, что хотите отключить 2FA для пользователя{" "}
                <strong>{disabling2FA.username}</strong>?
              </p>
              <p style={{ fontSize: 12, color: T.textMuted, marginTop: 12 }}>
                После отключения пользователь сможет войти без кода подтверждения.
              </p>
            </div>
            <div className="adm-modal__footer">
              <button className="adm-btn" onClick={() => setDisabling2FA(null)}>
                Отмена
              </button>
              <button className="adm-btn adm-btn--danger" onClick={() => disable2FA(disabling2FA)}>
                {Icon.Trash} Отключить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно смены роли */}
      {changingRole && (
        <div
          className="adm-modal-wrap"
          onClick={(e) => e.target === e.currentTarget && setChangingRole(null)}
        >
          <div className="adm-modal" style={{ maxWidth: 350 }}>
            <div className="adm-modal__header">
              <span className="adm-modal__title">Изменить роль</span>
              <button className="adm-btn adm-btn--sm" onClick={() => setChangingRole(null)}>
                {Icon.Close}
              </button>
            </div>
            <div className="adm-modal__body">
              <p style={{ marginBottom: 16, fontSize: 14, color: T.textMuted }}>
                Пользователь: <strong>{changingRole.username}</strong>
              </p>
              <div style={{ display: "flex", gap: 12 }}>
                <button
                  className={`adm-btn ${changingRole.role === "user" ? "adm-btn--primary" : ""}`}
                  onClick={() => changeRole(changingRole, "user")}
                  style={{ flex: 1 }}
                >
                  Пользователь
                </button>
                <button
                  className={`adm-btn ${changingRole.role === "admin" ? "adm-btn--primary" : ""}`}
                  onClick={() => changeRole(changingRole, "admin")}
                  style={{ flex: 1 }}
                >
                  Администратор
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно удаления */}
      {deleting && (
        <div
          className="adm-modal-wrap"
          onClick={(e) => e.target === e.currentTarget && setDeleting(null)}
        >
          <div className="adm-modal" style={{ maxWidth: 400 }}>
            <div className="adm-modal__header">
              <span className="adm-modal__title">Удаление пользователя</span>
              <button className="adm-btn adm-btn--sm" onClick={() => setDeleting(null)}>
                {Icon.Close}
              </button>
            </div>
            <div className="adm-modal__body">
              <p style={{ fontSize: 14, color: T.textMuted }}>
                Удалить пользователя <strong style={{ color: T.text }}>{deleting.username}</strong> (
                {deleting.email})? Это действие необратимо.
              </p>
            </div>
            <div className="adm-modal__footer">
              <button className="adm-btn" onClick={() => setDeleting(null)}>
                Отмена
              </button>
              <button className="adm-btn adm-btn--danger" onClick={() => confirmDelete(deleting.id)}>
                {Icon.Trash} Удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}